import { ShieldCheck } from "lucide-react";
import { useState } from "react";
import type { DowntimeReasonDraft, DowntimeReasonTarget } from "./downtime-reason-queue";
import type { ProductionReferenceData } from "./models";
import { TouchSheet } from "./TouchSheet";
import { useStoredOperatorDraft } from "./useStoredOperatorDraft";
import { reasonFormDraftSchema } from "./operator-drafts";
import { useDowntimeReasonApproval, type ReasonSubmitOptions } from "./useDowntimeReasonApproval";
import { DowntimeReasonChoice } from "./DowntimeReasonChoice";
import { downtimeReasonSubmission, type DowntimeReasonRequest } from "./downtime-reason-submission";
import { DowntimeQuickReasons } from "./DowntimeQuickReasons";
import { DowntimeKeyboardField } from "./DowntimeKeyboardField";
import { DowntimeVoiceEntry } from "./DowntimeVoiceEntry";

export function TouchDowntimeSheet({
  target,
  endedAt,
  terminalCode, sessionId, draftKey, preview,
  reasons,
  recentReasonIds = [],
  saving,
  draft: initialDraft,
  onDraftChange: onParentDraftChange,
  close,
  submit,
}: {
  target: DowntimeReasonTarget;
  endedAt: string;
  terminalCode: string; sessionId: string; draftKey: string; preview: boolean;
  reasons: ProductionReferenceData["reasons"];
  recentReasonIds?: string[];
  saving: boolean;
  draft: DowntimeReasonDraft;
  onDraftChange: (draft: DowntimeReasonDraft) => void;
  close: () => void;
  submit: (input: DowntimeReasonRequest, approvalToken: string | undefined, options: ReasonSubmitOptions) => Promise<unknown>;
}) {
  const stored = useStoredOperatorDraft(draftKey, preview, { kind: "downtime", ...initialDraft });
  const draft = reasonFormDraftSchema.parse(stored.data);
  const onDraftChange = (next: DowntimeReasonDraft) => { stored.change({ kind: "downtime", ...next }); onParentDraftChange(next); };
  const selectedReason = reasons.find((item) => item.id === draft.reasonCodeId);
  const noteRequired = selectedReason?.groupName === "Diğer";
  const [noteOpen, setNoteOpen] = useState(Boolean(draft.note));
  const [voiceBusy, setVoiceBusy] = useState(false);
  const {
    approvalRequired,
    supervisors,
    supervisorId,
    supervisorPin,
    setSupervisorPin,
    selectSupervisor,
    loadingSupervisors,
    approvalSaving,
    formError,
    retrySupervisors,
    save, cancel,
  } = useDowntimeReasonApproval({ target, endedAt, terminalCode, sessionId, saving, submit });
  const busy = saving || approvalSaving || stored.loading || voiceBusy;
  const dismiss = () => { cancel(); close(); };
  const saveDraft = async () => {
    if (busy) return;
    try {
      const record = await stored.flush();
      if (await save(reasonFormDraftSchema.parse(record.data), Boolean(noteRequired), record.intentId)) {
        // The server has confirmed the write. Do not keep the modal open while
        // the run list refreshes, or because local draft cleanup fails.
        close();
        void stored.clear().catch(() => undefined);
      }
    } catch { /* The draft hook renders storage errors; the approval hook renders API errors. */ }
  };

  return (
    <TouchSheet title="Duruş nedenini seçin" variant="downtime" close={dismiss} actions={
<footer className="touch-form-actions">
          <button type="button" className="touch-cancel" onClick={dismiss}>Daha sonra</button>
          <button
            type="button"
            className="touch-save is-stop"
            disabled={busy || stored.pending || Boolean(stored.error) || loadingSupervisors || !downtimeReasonSubmission(draft) || Boolean(noteRequired && !draft.note.trim()) || Boolean(approvalRequired && (!supervisorId || supervisorPin.length !== 4))}
            onClick={() => void saveDraft()}
          >
            {approvalSaving && approvalRequired ? "PIN doğrulanıyor…" : saving ? "Kaydediliyor…" : "Nedeni kaydet"}
          </button>
        </footer>
}>
      <div className="touch-form is-downtime">
        <p className={`touch-local-draft${stored.error ? " is-error" : ""}`} role="status">{stored.error ?? (stored.loading ? "Olayın taslağı yükleniyor…" : stored.pending ? "Taslak cihazda saklanıyor…" : "Seçiminiz yalnız bu duruşun ve bu oturumun cihaz taslağında korunur; sunucu kaydı değildir.")}
          {stored.error ? <button type="button" onClick={stored.reload}>Cihazdaki taslağa dön</button> : null}</p>
        <p className="touch-downtime-guidance">
          Tamamlanan duruşun nedenini seçin.
        </p>
        <p className="touch-downtime-event">
          Bitiş: <time dateTime={endedAt}>{new Date(endedAt).toLocaleString("tr-TR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}</time>
        </p>
        <DowntimeVoiceEntry draftKey={draftKey} reasons={reasons} draft={draft} onChange={onDraftChange} disabled={saving || approvalSaving || stored.loading} onBusyChange={setVoiceBusy} />
        <DowntimeQuickReasons reasons={reasons} ids={recentReasonIds} selectedId={draft.reasonCodeId}
          disabled={busy} onSelect={(reasonCodeId) => onDraftChange({ ...draft, reasonCodeId, customMode: false, customReason: "" })} />
        <DowntimeReasonChoice reasons={reasons} draft={draft} onChange={onDraftChange} disabled={busy} />

        {!noteOpen && !noteRequired && !draft.note ? <button type="button" className="touch-note-toggle" disabled={busy} onClick={() => setNoteOpen(true)}>Açıklama ekle</button> : null}
        {noteOpen || noteRequired || draft.note ? <DowntimeKeyboardField
          label={<>Açıklama <small>{noteRequired ? "Zorunlu" : "İsteğe bağlı"}</small></>}
          keyboardLabel="Açıklama için klavyeyi aç" multiline value={draft.note}
          required={noteRequired} maxLength={500} disabled={busy}
          onChange={(note) => onDraftChange({ ...draft, note })}
          placeholder={noteRequired ? "Seçilen diğer nedeni açıklayın" : "Duruşla ilgili kısa bir açıklama yazın"}
        /> : null}
        {approvalRequired ? (
          <section className="touch-downtime-approval" aria-labelledby="downtime-approval-title">
            <div>
              <ShieldCheck size={20} aria-hidden="true" />
              <span><strong id="downtime-approval-title">Vardiya amiri onayı</strong><small>Bu kayıt bir düzeltme veya inceleme gerektiriyor. Amir kendi PIN’ini girmeli.</small></span>
            </div>
            <label>
              <span>Vardiya amiri</span>
              <select value={supervisorId} disabled={busy || loadingSupervisors || !supervisors.length} onChange={(event) => selectSupervisor(event.target.value)}>
                {supervisors.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}
              </select>
            </label>
            <DowntimeKeyboardField label="Vardiya amiri PIN’i" keyboardLabel="Amir PIN'i için klavyeyi aç"
              value={supervisorPin} secret minLength={4} maxLength={4} disabled={busy || loadingSupervisors}
              onChange={(pin) => setSupervisorPin(pin.replace(/\D/g, "").slice(0, 4))} />
          </section>
        ) : null}
        {formError ? <div className="touch-downtime-feedback" role="alert"><p>{formError}</p>{approvalRequired && !supervisors.length && !loadingSupervisors ? <button type="button" onClick={retrySupervisors}>Amir listesini yeniden dene</button> : null}</div> : null}

      </div>
    </TouchSheet>
  );
}
